import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

import { cn } from '@core-ui-lib/utils';
import { Checkbox } from '@core-ui/Components/ui/checkbox';
import { Badge } from '@core-ui/Components/ui/badge';
import { Input } from '@core-ui/Components/ui/input';

import type { SdrCallSessionSummary } from '@features/wizard/types/wizard.types';
import { SessionsListSkeleton } from './SessionsSkeletons';
import CompactPagination from './CompactPagination';

interface SessionsListProps {
  sessions: SdrCallSessionSummary[];
  selectedSessionIds: string[];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onToggleSession: (id: string) => void;
  isLoading: boolean;
}

const buildSessionTitle = (session: SdrCallSessionSummary): string => {
  const listTitle = (session.listTitle ?? '').trim();
  if (listTitle !== '' && listTitle !== 'Not available') {
    return listTitle;
  }
  return 'Untitled session';
};

const formatCreatedAt = (createdAt: string | undefined, locale: string): string => {
  if (createdAt === undefined || createdAt === '') {
    return '';
  }
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
};

const SessionsList: React.FC<SessionsListProps> = ({
  sessions,
  selectedSessionIds,
  searchQuery,
  onSearchChange,
  onToggleSession,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredSessions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query === '') {
      return sessions;
    }
    return sessions.filter((session) => {
      const title = buildSessionTitle(session).toLowerCase();
      return title.includes(query) || session.callSessionId.toLowerCase().includes(query);
    });
  }, [sessions, searchQuery]);

  const totalItems = filteredSessions.length;
  const paginatedSessions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredSessions.slice(startIndex, startIndex + pageSize);
  }, [filteredSessions, currentPage, pageSize]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute h-4 w-4 left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            data-qa-id="sessions-search-bar"
            value={searchQuery}
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
            placeholder={t('wizard.selectContacts.sessions.searchPlaceholder', 'Search sessions...')}
            className="pl-9 pr-8 w-full"
          />
          {searchQuery !== '' && (
            <button
              type="button"
              onClick={() => {
                onSearchChange('');
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {selectedSessionIds.length > 0 && (
          <Badge className="bg-primary/15 text-primary hover:bg-primary/15 whitespace-nowrap">
            {t('wizard.selectContacts.sessions.selectedCount', { count: selectedSessionIds.length, defaultValue: '{{count}} selected' })}
          </Badge>
        )}
      </div>

      {isLoading ? (
        <SessionsListSkeleton rows={pageSize} />
      ) : paginatedSessions.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground rounded-lg border border-border border-dashed">
          {searchQuery.trim() !== ''
            ? t('wizard.selectContacts.sessions.empty.noResults', 'No sessions match your search.')
            : t('wizard.selectContacts.sessions.empty.title', 'No previous sessions')
          }
        </div>
      ) : (
        <>
          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full">
            {paginatedSessions.map((session) => {
              const isSelected = selectedSessionIds.includes(session.callSessionId);
              const title = buildSessionTitle(session);
              const createdLabel = formatCreatedAt(session.createdAt, t('locale.code', 'en-US'));
              return (
                <button
                  key={session.callSessionId}
                  type="button"
                  onClick={() => {
                    onToggleSession(session.callSessionId);
                  }}
                  className={cn(
                    'w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-border bg-card text-left transition-colors',
                    'hover:bg-muted/40',
                    isSelected && 'border-primary bg-primary/5',
                  )}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => {
                      onToggleSession(session.callSessionId);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-sm truncate min-w-0">{title}</span>
                      {isSelected && (
                        <Badge className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/15 shrink-0">
                          {t('wizard.selected', 'Selected')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 font-mono break-all">{session.callSessionId}</p>
                    {createdLabel !== '' && (
                      <p className="text-xs text-muted-foreground mt-0.5">{createdLabel}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {totalItems > 0 && (
            <CompactPagination
              totalItems={totalItems}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={(p) => {
                setCurrentPage(Math.max(1, Math.min(p, Math.ceil(totalItems / pageSize))));
              }}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setCurrentPage(1);
              }}
              rowsPerPageOptions={[5, 10, 15]}
              className="pt-2"
            />
          )}
        </>
      )}
    </div>
  );
};

export default SessionsList;
